import React, { useState } from 'react';
import { Card, Button, Row, Col, Nav, Badge } from 'react-bootstrap';
import { FaFileAlt, FaFileInvoiceDollar, FaExchangeAlt, FaReceipt, FaFolder, FaDownload, FaEnvelope, FaTrash, FaImage, FaFilePdf, FaFileWord, FaFileImage } from 'react-icons/fa';
import { downloadDocument } from '../../utils/documentUtils';
import { toast } from 'react-toastify';

const DocumentsCardGrid = ({ documents, receipts, token, onDelete, clientEmail }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Categorize documents
  const categorizeDocument = (doc) => {
    const name = doc.DocumentName?.toLowerCase() || '';
    const type = doc.DocumentType?.toLowerCase() || '';
    
    if (name.includes('proposal') || type.includes('proposal')) return 'proposals';
    if (name.includes('invoice') || type.includes('invoice')) return 'invoices';
    if (name.includes('change') || name.includes('order') || type.includes('change')) return 'changeOrders';
    return 'other';
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return <FaFilePdf className="text-danger" size={40} />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord className="text-primary" size={40} />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <FaFileImage className="text-success" size={40} />;
    return <FaFileAlt className="text-secondary" size={40} />;
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'proposals': return <FaFileAlt className="text-primary" />;
      case 'invoices': return <FaFileInvoiceDollar className="text-success" />;
      case 'changeOrders': return <FaExchangeAlt className="text-warning" />;
      case 'receipts': return <FaReceipt className="text-info" />;
      default: return <FaFolder className="text-secondary" />;
    }
  };

  // Get category color
  const getCategoryColor = (category) => {
    switch (category) {
      case 'proposals': return '#0d6efd';
      case 'invoices': return '#198754';
      case 'changeOrders': return '#fd7e14';
      case 'receipts': return '#0dcaf0';
      default: return '#6c757d';
    }
  };

  // Filter documents
  const getFilteredItems = () => {
    let filteredDocs = documents.map(doc => ({ ...doc, itemType: 'document', category: categorizeDocument(doc) }));
    let filteredReceipts = receipts.map(r => ({ ...r, itemType: 'receipt', category: 'receipts' }));
    
    let allItems = [...filteredDocs, ...filteredReceipts];
    
    if (activeFilter === 'all') return allItems;
    return allItems.filter(item => item.category === activeFilter);
  };

  // Count by category
  const getCounts = () => {
    const counts = { all: 0, proposals: 0, invoices: 0, changeOrders: 0, receipts: receipts.length, other: 0 };
    documents.forEach(doc => {
      const cat = categorizeDocument(doc);
      counts[cat]++;
      counts.all++;
    });
    counts.all += receipts.length;
    return counts;
  };

  const counts = getCounts();
  const filteredItems = getFilteredItems();

  const renderDocumentCard = (doc) => (
    <Col key={doc.DocumentId} xs={12} sm={6} md={4} lg={3} className="mb-4">
      <Card className="h-100 shadow-sm document-card" style={{ borderTop: `4px solid ${getCategoryColor(doc.category)}` }}>
        <Card.Body className="text-center">
          <div className="mb-3 py-3 bg-light rounded">
            {getFileIcon(doc.DocumentName)}
          </div>
          <Card.Title className="fs-6 text-truncate" title={doc.DocumentName}>
            {doc.DocumentName}
          </Card.Title>
          <div className="mb-2">
            <Badge bg="light" text="dark" className="me-1">
              {getCategoryIcon(doc.category)}
              <span className="ms-1 text-capitalize">{doc.category.replace(/([A-Z])/g, ' $1').trim()}</span>
            </Badge>
          </div>
          <small className="text-muted d-block mb-3">
            {new Date(doc.CreatedAt).toLocaleDateString()}
          </small>
        </Card.Body>
        <Card.Footer className="bg-white border-top-0">
          <div className="d-flex justify-content-center gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => downloadDocument(doc.DocumentId, doc.DocumentName, token)}
              title="Download"
            >
              <FaDownload />
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              disabled={!clientEmail}
              title={!clientEmail ? "No client email" : "Email to client"}
            >
              <FaEnvelope />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onDelete(doc.DocumentId)}
              title="Delete"
            >
              <FaTrash />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Col>
  );

  const renderReceiptCard = (receipt) => (
    <Col key={receipt.ScanId} xs={12} sm={6} md={4} lg={3} className="mb-4">
      <Card className="h-100 shadow-sm document-card" style={{ borderTop: `4px solid ${getCategoryColor('receipts')}` }}>
        <Card.Body className="text-center">
          <div className="mb-3 py-3 bg-light rounded">
            <FaReceipt className="text-info" size={40} />
          </div>
          <Card.Title className="fs-6 text-truncate" title={receipt.StoreName}>
            {receipt.StoreName || 'Unknown Store'}
          </Card.Title>
          <div className="mb-2">
            <Badge 
              bg={receipt.Status === 'approved' ? 'success' : receipt.Status === 'pending' ? 'warning' : 'secondary'}
            >
              {receipt.Status}
            </Badge>
          </div>
          <div className="text-muted small">
            <div>${parseFloat(receipt.TotalAmount || 0).toFixed(2)}</div>
            <div>{receipt.ItemCount || 0} items</div>
          </div>
          <small className="text-muted d-block mt-2">
            {new Date(receipt.ScannedAt).toLocaleDateString()}
          </small>
        </Card.Body>
        <Card.Footer className="bg-white border-top-0">
          <div className="d-flex justify-content-center gap-1">
            <Button
              variant="outline-info"
              size="sm"
              title="View Receipt Image"
            >
              <FaImage />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Col>
  );

  return (
    <div>
      {/* Filter Tabs */}
      <Nav variant="pills" className="mb-4 flex-wrap">
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'all'} 
            onClick={() => setActiveFilter('all')}
            className="me-2 mb-2"
          >
            All <Badge bg="secondary" className="ms-1">{counts.all}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'proposals'} 
            onClick={() => setActiveFilter('proposals')}
            className="me-2 mb-2"
          >
            <FaFileAlt className="me-1" /> Proposals <Badge bg="primary" className="ms-1">{counts.proposals}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'invoices'} 
            onClick={() => setActiveFilter('invoices')}
            className="me-2 mb-2"
          >
            <FaFileInvoiceDollar className="me-1" /> Invoices <Badge bg="success" className="ms-1">{counts.invoices}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'changeOrders'} 
            onClick={() => setActiveFilter('changeOrders')}
            className="me-2 mb-2"
          >
            <FaExchangeAlt className="me-1" /> Change Orders <Badge bg="warning" className="ms-1">{counts.changeOrders}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'receipts'} 
            onClick={() => setActiveFilter('receipts')}
            className="me-2 mb-2"
          >
            <FaReceipt className="me-1" /> Receipts <Badge bg="info" className="ms-1">{counts.receipts}</Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeFilter === 'other'} 
            onClick={() => setActiveFilter('other')}
            className="me-2 mb-2"
          >
            <FaFolder className="me-1" /> Other <Badge bg="secondary" className="ms-1">{counts.other}</Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Cards Grid */}
      {filteredItems.length > 0 ? (
        <Row>
          {filteredItems.map(item => 
            item.itemType === 'receipt' 
              ? renderReceiptCard(item)
              : renderDocumentCard(item)
          )}
        </Row>
      ) : (
        <div className="text-center py-5 text-muted">
          <FaFolder size={48} className="mb-3" />
          <p>No documents found</p>
        </div>
      )}

      <style>{`
        .document-card {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .document-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </div>
  );
};

export default DocumentsCardGrid;
